import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Messages } from '../../codegen/Localize';
import { ActionDialogBodyWithInlineErrorMessage, ActionDialogBodyWithInlineErrorMessageProps, ActionDialogBodyWithInlineErrorMessageState } from '../../commonInfra/dialogs/ActionDialogBodyWithInlineErrorMessage';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { UpdateStringServerSettingRequest } from '../../settings/ServerApi/requests/serverSettings/UpdateStringServerSettingRequest';
import { ServerUsers } from '../../userManagement/services/resources/ServerUsers';
import { IUserInfo } from '../../userManagement/users/models/IUserInfo';
import { UserSettings } from '../../userSettings/services/resources/UserSettings';
import { updateUserSettingsAction } from '../actions/UpdateUserSettingsAction';
import { IGetUserSettingsResult } from '../serverApi/requests/GetUserSettingsRequest';
import { UpdateSiteStartPageRequest } from '../serverApi/requests/UpdateSiteStartPageRequest';
import { StartPageService } from './startPage/StartPageService';

const initialDialogState: ActionDialogBodyWithInlineErrorMessageState = {
};

function confirmChangeUserStartPage() {
  const initialBodyProps: ActionDialogBodyWithInlineErrorMessageProps = { dialogMessage: Messages.userStartPageConfirm() };
  openReactActionDialog({
    title: Messages.userStartPageChange(),
    actionName: Messages.userStartPageButton(),
    action: (_, state, updateDialogState) => {
      return updateCurrentUsersStartPage(StartPageService.getCurrentUrlAsStartPage())
        .then((result: any) => {
          if (!result || result.errors) {
            updateDialogState({ errorMessage: Messages.userStartPageError() });
            return Promise.reject();
          }
        });
    },
    dialogWidth: TabDialogWidth.width375px,
    bodyComponent: ActionDialogBodyWithInlineErrorMessage,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState
  });
}

function confirmChangeSiteStartPage() {
  const server = getSessionManagementServiceInstance();
  const initialBodyProps: ActionDialogBodyWithInlineErrorMessageProps = { dialogMessage: Messages.siteStartPageConfirm({ SITE_NAME: server.sessionSiteName() }) };
  openReactActionDialog({
    title: Messages.siteStartPageChange(),
    actionName: Messages.siteStartPageButton(),
    action: (_, state, updateDialogState) => {
      return updateSiteStartPage(StartPageService.getCurrentUrlAsStartPage())
        .then((result: any) => {
          if (!result || result.errors) {
            updateDialogState({ errorMessage: Messages.userStartPageError() });
            return Promise.reject();
          }
        });
    },
    dialogWidth: TabDialogWidth.width375px,
    bodyComponent: ActionDialogBodyWithInlineErrorMessage,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState
  });
}

function confirmChangeServerDefaultStartPage() {
  const server = getSessionManagementServiceInstance();
  let toaster = ToasterService.instance;
  const initialBodyProps: ActionDialogBodyWithInlineErrorMessageProps = { dialogMessage: Messages.defaultStartPageConfirm() };
  openReactActionDialog({
    title: Messages.defaultStartPageChange(),
    actionName: Messages.defaultStartPageButton(),
    action: (_, state, updateDialogState) => {
      return WebHttpClient.sendRequest(new UpdateStringServerSettingRequest({
        setting: 'defaultStartPage',
        value: StartPageService.getCurrentUrlAsStartPage()
      })).then((result: any) => {
        if (!result || result.errors) {
          updateDialogState({ errorMessage: Messages.defaultStartPageError() });
          return Promise.reject();
        } else {
          server.refreshSessionInfo().then(() => {
            toaster.pop({ type: ToastType.Info, text: Messages.defaultStartPageSuccess() });
          });
        }
      });
    },
    dialogWidth: TabDialogWidth.width375px,
    bodyComponent: ActionDialogBodyWithInlineErrorMessage,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState
  });
}

function confirmResetServerDefaultStartPage(callback: (newStartPage: string) => void) {
  let toaster = ToasterService.instance;
  const initialBodyProps: ActionDialogBodyWithInlineErrorMessageProps = { dialogMessage: Messages.resetStartPage_dialog_message() };
  openReactActionDialog({
    title: Messages.resetStartPage_dialog_title(),
    actionName: Messages.defaultStartPageButton(),
    action: (_, state, updateDialogState) => {
      return WebHttpClient.sendRequest(new UpdateStringServerSettingRequest({
        setting: 'defaultStartPage',
        value: StartPageService.getServerDefaultStartPage()
      })).then((result: any) => {
        if (!result || result.errors) {
          updateDialogState({ errorMessage: Messages.defaultStartPageError() });
          return Promise.reject();
        } else {
          toaster.pop({ type: ToastType.Info, text: Messages.defaultStartPageSuccess() });
          if (callback) {
            callback(StartPageService.getServerDefaultStartPage());
          }
        }
      });
    },
    dialogWidth: TabDialogWidth.width375px,
    bodyComponent: ActionDialogBodyWithInlineErrorMessage,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState
  });
}

/**
 * Resets the site start page.
 * Callback is called on success.
 *
 * The optional siteId allows reset to be applied to any specified site, but must only
 * be used by Server Admin!
 */
function confirmResetSiteDefaultStartPage(callback: () => void, siteId?: string) {
  let toaster = ToasterService.instance;
  const initialBodyProps: ActionDialogBodyWithInlineErrorMessageProps = { dialogMessage: Messages.resetStartPage_dialog_message() };
  openReactActionDialog({
    title: Messages.resetStartPage_dialog_title(),
    actionName: Messages.siteStartPageButton(),
    action: (_, state, updateDialogState) => {
      return WebHttpClient.sendRequest(new UpdateSiteStartPageRequest({
        startPage: '',
        siteId
      })).then((result: any) => {
        if (!result || result.errors) {
          updateDialogState({ errorMessage: Messages.resetSiteStartPageError() });
          return Promise.reject();
        } else {
          toaster.pop({ type: ToastType.Info, text: Messages.defaultStartPageSuccess() });
          if (callback) {
            callback();
          }
        }
      });
    },
    dialogWidth: TabDialogWidth.width375px,
    bodyComponent: ActionDialogBodyWithInlineErrorMessage,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState
  });
}

function updateCurrentUsersStartPage(startPage: string) {
  const server = getSessionManagementServiceInstance();
  // TODO: error handling for this action is still handled by callers. Fix that. Then there will be no need to return this promise.
  return UserSettings.updateCurrentUsersStartPage(startPage)
    .then((result: IResult) => {
      if (result && !result.errors) {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.userStartPageSuccess() });
        fetchUserSettings(server.sessionUsername(), server.sessionUserDomainName());
      }
      return result;
    });
}

function updateSiteStartPage(startPage: string) {
  const server = getSessionManagementServiceInstance();
  return UserSettings.updateSiteStartPage(startPage)
    .then((result: IResult) => {
      if (result && !result.errors) {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.siteStartPageSuccess() });
        fetchUserSettings(server.sessionUsername(), server.sessionUserDomainName());
      }
      return result;
    });
}

function resetStartPage(user: IUserInfo): Promise<any> {
  // TODO: error handling for this action is still handled by callers. Fix that. Then there will be no need to return this promise.
  return UserSettings.resetUserStartPage(user.id).then((result: IResult) => {
    if (result && !result.errors) {
      ToasterService.instance.pop({ type: ToastType.Info, text: Messages.resetStartPageSuccess() });
      fetchUserSettings(user.username, user.domainName);
    }
    return result;
  });
}

function fetchUserSettings(username: string, domain: string): Promise<IGetUserSettingsResult> {
  return ServerUsers.instance.getUserSettings(username, domain).then((result: IGetUserSettingsResult) => {
    // Some subscribers interpret the change to user settings as a signal that the session info has been updated,
    // so we ensure that it has before dispatching the update.
    // TODO: Somehow make this unnecessary?
    return maybeRefreshSessionInfo(username, domain).then(() => {
      getAppStore().dispatch(updateUserSettingsAction(username, domain, result));
      return result;
    });
  });
}

function forgetUserSettings(username: string, domain: string) {
  getAppStore().dispatch(updateUserSettingsAction(username, domain, undefined));
}

function maybeRefreshSessionInfo(username: string, domain: string): Promise<void> {
  const server = getSessionManagementServiceInstance();
  if (username === server.sessionUsername() && domain === server.sessionUserDomainName()) {
    return server.refreshSessionInfo();
  } else {
    return Promise.resolve();
  }
}


export const UserSettingsActions = {
  confirmChangeUserStartPage,
  confirmChangeSiteStartPage,
  confirmChangeServerDefaultStartPage,
  confirmResetServerDefaultStartPage,
  confirmResetSiteDefaultStartPage,
  resetStartPage,
  fetchUserSettings,
  forgetUserSettings
};
